import React, { useEffect, useMemo, useState } from 'react'
import { APPT_STATUS_MENU_ITEMS } from '@constants/AppointmentPage'
import { AppointmentStatus } from '@dataTypes/appointment.type'
import { AppointmentStatusMap } from '@utils/appointment.util'
import './AppointmentStatusBadgeSelect.css'
import { Select } from '@enamelai/design-system'

type AppointmentStatusBadgeSelectProps = {
  status: AppointmentStatus
  onChange: (status: AppointmentStatus) => void
}

const STATUS_OPTIONS = Object.entries(APPT_STATUS_MENU_ITEMS)
  .map(([value, item]) => ({
    value: Number(value) as AppointmentStatus,
    label: item.text,
    position: item.position,
  }))
  .sort((a, b) => a.position - b.position)

const STATUS_COLOR_MAP: Record<AppointmentStatus, string> = {
  [AppointmentStatus.Confirmed]: 'app-badge--tinted-pale-green',
  [AppointmentStatus.Completed]: 'app-badge--tinted-blue',
  [AppointmentStatus.Pending]: 'app-badge--tinted-gray',
  [AppointmentStatus.Alert]: 'app-badge--tinted-red',
  [AppointmentStatus.FollowUp]: 'app-badge--tinted-blue',
  [AppointmentStatus.NewPatient]: 'app-badge--tinted-teal',
  [AppointmentStatus.Cancelled]: 'app-badge--tinted-red',
  [AppointmentStatus.NoShow]: 'app-badge--tinted-yellow',
  [AppointmentStatus.Late]: 'app-badge--tinted-red',
  [AppointmentStatus.Unconfirmed]: 'app-badge--solid-yellow',
  [AppointmentStatus.CheckIn]: 'app-badge--tinted-teal',
  [AppointmentStatus.InTreatment]: 'app-badge--solid-blue',
  [AppointmentStatus.CheckOut]: 'app-badge--solid-cyan',
}

const AppointmentStatusBadgeSelect = ({
  status,
  onChange,
}: AppointmentStatusBadgeSelectProps): React.ReactElement => {
  const [currentStatus, setCurrentStatus] = useState(status)

  useEffect(() => {
    setCurrentStatus(status)
  }, [status])

  const statusClass = STATUS_COLOR_MAP[currentStatus]
  const badgeStatus = useMemo(
    () => AppointmentStatusMap[currentStatus ?? 2],
    [currentStatus],
  )

  return (
    <Select
      className={`ai-receptionist-appt-status-select ${statusClass}`}
      placeholder="Select appointment status"
      value={badgeStatus as unknown as string}
      options={STATUS_OPTIONS.map((option) => ({
        value: option.value as unknown as string,
        label: option.label,
      }))}
      onChange={(value) => onChange(value as unknown as AppointmentStatus)}
    />
  )
}

export default AppointmentStatusBadgeSelect
